import { fechaCierreVigente, fechaEstaCerrada, saveData, siguienteNumeroDoc, getParametrosFiscales } from "./cloudSync.js";
import { EMPRESA } from "./config.js";
import { state } from "./state.js";
import { armablesDeKit, esProductoKit, productoPorNombre } from "./kitCatalogo.js";
import { componentesDeArticulo } from "./kit.js";
import { apartadoVigentePorPedido } from "./recepcion.js";
import { fmtCOP, fmtDate, genId, parseNumber, parsePct, today, todayCO } from "./utils.js";

// ─── Encontrar la ficha de inventario de un artículo ────────────────────────
// La comparación era por nombre EXACTO, y en la práctica los nombres llegan con
// diferencias que no cambian de qué producto se habla: un espacio al final
// ("Set de memos "), mayúsculas distintas, o el nombre guardado con la
// descripción pegada detrás ("Sticker Sticker impresos en papel adhesivo…"
// contra "Sticker" en la orden). Con la comparación exacta el ERP daba stock 0
// y escondía el botón de remisionar aunque la mercancía estuviera ingresada.
//
// Se busca en tres pasos, de más estricto a menos: idéntico, igual salvo
// espacios/mayúsculas/tildes, y —solo si hay UNA sola candidata, para no
// adivinar— la ficha cuyo nombre empieza por el del artículo.
function normNombre(s) {
  return String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .toLowerCase().replace(/\s+/g, " ").trim();
}

export function indiceInventario(lista, articulo) {
  if (!articulo) return -1;
  const exacto = lista.findIndex(i => i.articulo === articulo);
  if (exacto >= 0) return exacto;
  const n = normNombre(articulo);
  if (!n) return -1;
  const laxo = lista.findIndex(i => normNombre(i.articulo) === n);
  if (laxo >= 0) return laxo;
  if (n.length < 4) return -1;   // un prefijo muy corto casa con cualquier cosa
  const candidatos = [];
  lista.forEach((i, idx) => { if (normNombre(i.articulo).startsWith(n)) candidatos.push(idx); });
  return candidatos.length === 1 ? candidatos[0] : -1;
}

export function fichaInventario(articulo) {
  const inv = state.data.inventario || [];
  const idx = indiceInventario(inv, articulo);
  return idx >= 0 ? inv[idx] : null;
}

// Un kit puede estar hecho de partes que son, a su vez, kits. Eso funciona,
// pero un ciclo (A lleva B y B lleva A) haría que el cálculo se llamara a sí
// mismo para siempre y colgaría el navegador. `visitados` corta el ciclo: un
// artículo que ya está en la cadena vale cero, así que el kit que lo contenga
// sale en cero en vez de trabar la aplicación. La ficha impide armar el ciclo
// desde la interfaz; esto lo hace imposible por cualquier vía.
export function getStockProducto(articulo, visitados) {
  const cadena = visitados || new Set();
  const clave = normNombre(articulo);
  if (cadena.has(clave)) return 0;
  // Un kit de catálogo NO es inventariable: no tiene ficha de kardex porque no
  // se compra ni se almacena. Su stock es cuántas unidades se pueden ARMAR con
  // lo que hay de sus partes, y se calcula aquí para que todo el ERP —las
  // líneas de la cotización, el bloqueo de la remisión, los informes— lo vea
  // sin tener que saber que el artículo es un kit.
  const prod = productoPorNombre(articulo);
  if (prod && prod.es_kit) {
    cadena.add(clave);
    const armables = armablesDeKit(prod, a => getStockProducto(a, cadena));
    cadena.delete(clave);
    if (armables != null) return armables;
  }
  const item = fichaInventario(articulo);
  return item ? (parseNumber(item.stock) || 0) : 0;
}

// ─── Mercancía comprometida: facturada y todavía en bodega ───────────────────
// El inventario se descuenta SIEMPRE con la remisión, no con la factura. Pero
// entre facturar y entregar —un cliente que pide facturar el cierre de mes y
// recibir a los ocho días— esas unidades siguen en el kardex, y sin marcarlas
// otro asesor las promete a su cliente y despacha las últimas que había. La
// mercancía está vendida: sigue contando en el inventario, pero no se le puede
// prometer a nadie más.
//
// La cantidad comprometida es lo facturado que aún no ha salido, que es lo mismo
// que la factura lleva anotado para costear al entregar (`_costoPendiente`): se
// llena al facturar sin entregar y se consume cuando la mercancía sale.
export function getComprometidoPorEntregar(articulo, exceptoOpId) {
  const esDeLaOP = (f) => !!exceptoOpId && [f._origen, ...(Array.isArray(f._origenes) ? f._origenes : [])]
    .filter(Boolean).some(o => o.tipo === "OP" && o.id === exceptoOpId);
  return (state.data.facturas || [])
    .filter(f => f.estado !== "anulada" && (f._costoPendiente || []).length && !esDeLaOP(f))
    .reduce((sum, f) => sum + (f._costoPendiente || [])
      .filter(x => normNombre(x.articulo) === normNombre(articulo))
      .reduce((s, x) => s + (parseNumber(x.cantidad) || 0), 0), 0);
}

// Quién tiene comprometida la mercancía, para poder decirlo por su nombre en vez
// de dejar al asesor adivinando por qué el inventario "no alcanza".
export function comprometidosDe(articulo, exceptoOpId) {
  const esDeLaOP = (f) => !!exceptoOpId && [f._origen, ...(Array.isArray(f._origenes) ? f._origenes : [])]
    .filter(Boolean).some(o => o.tipo === "OP" && o.id === exceptoOpId);
  return (state.data.facturas || [])
    .filter(f => f.estado !== "anulada" && (f._costoPendiente || []).length && !esDeLaOP(f))
    .map(f => {
      const cantidad = (f._costoPendiente || [])
        .filter(x => normNombre(x.articulo) === normNombre(articulo))
        .reduce((s, x) => s + (parseNumber(x.cantidad) || 0), 0);
      const cli = (state.data.clientes || []).find(c => c.id === f.cliente_id);
      return { factura: f.numero, cliente: (cli && cli.nombre) || f.cliente_nombre || "", cantidad };
    })
    .filter(x => x.cantidad > 0);
}

// ─── Mercancía apartada para otro pedido (flujo piloto) ──────────────────────
// La recepción del piloto le APARTA a cada pedido lo que le tocó, sin
// vencimiento. Esa mercancía está en bodega pero tiene dueño, igual que la
// facturada sin entregar, así que el flujo de siempre tampoco puede prometerla
// ni despacharla a otra orden. Lo vigente sale de la misma regla del piloto
// (`apartadoVigentePorPedido`): nunca pasa de lo que el pedido aún debe
// entregar, así que su propia remisión lo consume sola.
//
// Si el pedido dueño además ya FACTURÓ esas unidades sin entregarlas, son las
// mismas unidades contadas dos veces: se reserva lo mayor de las dos cifras,
// no la suma.
function comprometidoDeOP(articulo, opId) {
  return (state.data.facturas || [])
    .filter(f => f.estado !== "anulada" && (f._costoPendiente || []).length &&
      [f._origen, ...(Array.isArray(f._origenes) ? f._origenes : [])].filter(Boolean).some(o => o.tipo === "OP" && o.id === opId))
    .reduce((sum, f) => sum + (f._costoPendiente || [])
      .filter(x => normNombre(x.articulo) === normNombre(articulo))
      .reduce((s, x) => s + (parseNumber(x.cantidad) || 0), 0), 0);
}

export function apartadosAjenos(articulo, exceptoOpId) {
  const d = state.data;
  if (!(d.apartados || []).length) return [];
  const ctx = {
    pedidos: d.pedidos || [], apartados: d.apartados, ocp: d.ocp || [],
    remisiones: d.remisiones || [], facturas: d.facturas || [],
    componentes: (op, a) => componentesDeArticulo(op, a)
  };
  return apartadoVigentePorPedido(articulo, ctx)
    .filter(x => x.pedido_id !== exceptoOpId)
    .map(x => ({ ...x, cantidad: Math.max(0, x.cantidad - comprometidoDeOP(articulo, x.pedido_id)) }))
    .filter(x => x.cantidad > 0);
}

export function getApartadoAjeno(articulo, exceptoOpId) {
  return apartadosAjenos(articulo, exceptoOpId).reduce((s, x) => s + x.cantidad, 0);
}

// Lo que se le puede prometer a un cliente nuevo: lo que hay menos lo que ya
// está vendido y pendiente de entregar, y menos lo apartado para OTROS pedidos.
// `exceptoOpId` deja fuera lo comprometido y lo apartado por la propia orden
// que se está despachando —esa mercancía es justamente la que se va a
// entregar, no un estorbo.
export function getStockDisponible(articulo, exceptoOpId, visitados) {
  const cadena = visitados || new Set();
  const clave = normNombre(articulo);
  if (cadena.has(clave)) return 0;          // ver el comentario de getStockProducto
  // Para un kit se arma con lo DISPONIBLE de cada parte, no con el stock bruto:
  // un cuaderno que ya está facturado y pendiente de entregar no sirve para
  // armarle un set a otro cliente.
  const prod = productoPorNombre(articulo);
  if (prod && prod.es_kit) {
    cadena.add(clave);
    const armables = armablesDeKit(prod, a => getStockDisponible(a, exceptoOpId, cadena));
    cadena.delete(clave);
    if (armables != null) return armables;
  }
  return getStockProducto(articulo) - getComprometidoPorEntregar(articulo, exceptoOpId)
    - getApartadoAjeno(articulo, exceptoOpId);
}

// ─── Anticipos de clientes: cuánto queda vivo ───────────────────────────────
// Un anticipo casi nunca calza exacto con la factura. El caso típico: el cliente
// gira el bruto por adelantado y después la factura sale con retención, así que
// sobra justo lo retenido. Ese sobrante es plata del cliente —pasivo 2710 vivo—
// y hay que poder devolverla o dejarla para su próxima compra, no forzarla
// contra una factura que ya está saldada (eso dejaría la cartera en negativo).
//
// De ahí que un anticipo se consuma por partes: lo aplicado a facturas y lo
// devuelto. `_cruzado` se conserva como "ya no queda nada" para los anticipos
// registrados antes de que existiera el cruce parcial.
export function anticipoAplicado(a) {
  if (!a) return 0;
  if (Array.isArray(a._aplicaciones)) return a._aplicaciones.reduce((s, x) => s + (parseNumber(x.monto) || 0), 0);
  return a._cruzado ? (parseNumber(a.monto) || 0) : 0;
}

export function anticipoDevuelto(a) {
  if (!a || !Array.isArray(a._devoluciones)) return 0;
  return a._devoluciones.reduce((s, x) => s + (parseNumber(x.monto) || 0), 0);
}

export function anticipoDisponible(a) {
  return Math.max(0, (parseNumber(a && a.monto) || 0) - anticipoAplicado(a) - anticipoDevuelto(a));
}

// Todos los anticipos vivos de un cliente, venga de la orden que venga: el
// dinero es del cliente, no de la orden donde se recibió, así que un saldo a
// favor tiene que poder aplicarse a la compra siguiente. Los de la orden que se
// está facturando van de primeras, que son los que casi siempre se buscan.
export function anticiposVivosCliente(clienteId, opPreferidaId) {
  if (!clienteId) return [];
  const vivos = [];
  (state.data.pedidos || []).forEach(op => {
    if (op.cliente_id !== clienteId) return;
    (op._anticipos || []).forEach(ant => {
      const disponible = anticipoDisponible(ant);
      if (disponible > 0) vivos.push({ op, ant, disponible, propia: op.id === opPreferidaId });
    });
  });
  return vivos.sort((x, y) => (y.propia ? 1 : 0) - (x.propia ? 1 : 0) ||
                              String(x.ant.fecha || "").localeCompare(String(y.ant.fecha || "")));
}

export function getCostoPromedio(articulo) {
  const item = fichaInventario(articulo);
  if (item && parseNumber(item.costo_promedio) > 0) return parseNumber(item.costo_promedio);
  const prod = (state.data.productos||[]).find(p => p.nombre === articulo);
  return prod ? (parseNumber(prod.costo) || 0) : 0;
}

export function ingresarInventario(articulo, cantidad, costoUnitario, origen, fecha) {
  // Un kit no entra al kardex: lo que se compra y se almacena son sus partes.
  // Crearle una ficha le daría existencias propias que la remisión nunca va a
  // sacar (saca los componentes), y quedarían ahí para siempre.
  if (esProductoKit(productoPorNombre(articulo))) {
    console.warn(`Ingreso ignorado: "${articulo}" es un kit y no es inventariable. Ingresa sus componentes.`);
    return;
  }
  if (!articulo || !cantidad || cantidad <= 0) return;
  const inv   = (state.data.inventario||[]).map(i => ({...i}));
  const idx   = indiceInventario(inv, articulo);
  const hoy   = fecha || today();

  if (idx >= 0) {
    const stockAnt  = parseNumber(inv[idx].stock)          || 0;
    const cpAnt     = parseNumber(inv[idx].costo_promedio) || costoUnitario;
    const valorAnt  = stockAnt * cpAnt;
    const stockNuevo= stockAnt + cantidad;
    const cpNuevo   = stockNuevo > 0 ? (valorAnt + cantidad * costoUnitario) / stockNuevo : costoUnitario;
    inv[idx].stock          = stockNuevo;
    inv[idx].costo_promedio = Math.round(cpNuevo * 100) / 100;
    inv[idx].valor_total    = Math.round(stockNuevo * cpNuevo);
    if (!inv[idx]._movimientos) inv[idx]._movimientos = [];
    inv[idx]._movimientos.push({
      id: genId(), tipo:"entrada", fecha: hoy,
      cantidad, costo_unitario: costoUnitario,
      costo_promedio_resultante: inv[idx].costo_promedio,
      stock_resultante: stockNuevo,
      valor_total: Math.round(cantidad * costoUnitario),
      origen: origen || null
    });
  } else {
    inv.push({
      articulo, stock: cantidad,
      costo_promedio: costoUnitario,
      valor_total: Math.round(cantidad * costoUnitario),
      _movimientos: [{
        id: genId(), tipo:"entrada", fecha: hoy,
        cantidad, costo_unitario: costoUnitario,
        costo_promedio_resultante: costoUnitario,
        stock_resultante: cantidad,
        valor_total: Math.round(cantidad * costoUnitario),
        origen: origen || null
      }]
    });
  }
  saveData("inventario", inv);
  // Sincronizar el campo costo del catálogo de productos con el nuevo costo promedio
  const prods = (state.data.productos||[]).map(p =>
    p.nombre === articulo ? { ...p, costo: Math.round((inv[indiceInventario(inv, articulo)]||{}).costo_promedio||p.costo) } : p
  );
  saveData("productos", prods);
}

export function egresarInventario(articulo, cantidad, origen, fecha) {
  if (!articulo || !cantidad || cantidad <= 0) return 0;
  const inv   = (state.data.inventario||[]).map(i => ({...i}));
  const idx   = indiceInventario(inv, articulo);
  const hoy   = fecha || today();
  const cp    = getCostoPromedio(articulo);
  const costoSalida = Math.round(cantidad * cp);

  if (idx >= 0) {
    const stockNuevo = Math.max(0, (parseNumber(inv[idx].stock)||0) - cantidad);
    inv[idx].stock       = stockNuevo;
    inv[idx].valor_total = Math.round(stockNuevo * cp);
    if (!inv[idx]._movimientos) inv[idx]._movimientos = [];
    inv[idx]._movimientos.push({
      id: genId(), tipo:"salida", fecha: hoy,
      cantidad: -cantidad, costo_unitario: cp,
      costo_promedio_resultante: cp,
      stock_resultante: stockNuevo,
      valor_total: -costoSalida,
      origen: origen || null
    });
    saveData("inventario", inv);
  }
  return costoSalida;
}

// Corrige el VALOR del inventario sin mover cantidades, y recalcula el costo
// promedio. Es lo que hace falta cuando la mercancía ya entró a un costo y la
// factura del proveedor llega por otro valor (un descuento, un reajuste): las
// unidades son las mismas, lo que cambia es lo que costaron.
//
// `deltaValor` negativo abarata (descuento), positivo encarece.
//
// No genera asiento: el asiento de la causación ya se hizo por el valor REAL de
// la factura. Esto solo alinea el módulo de inventario con esa realidad.
export function ajustarCostoInventario(articulo, deltaValor, origen, fecha) {
  if (!articulo || !deltaValor) return null;
  const inv = (state.data.inventario || []).map(i => ({ ...i }));
  const idx = indiceInventario(inv, articulo);
  if (idx < 0) return null;                     // no está en inventario: nada que corregir
  const stock    = parseNumber(inv[idx].stock) || 0;
  const valorAnt = parseNumber(inv[idx].valor_total) || 0;
  const cpAnt    = parseNumber(inv[idx].costo_promedio) || 0;
  // El valor no puede quedar negativo: un descuento mayor que el valor en
  // libros solo puede llevarlo a cero.
  const valorNuevo = Math.max(0, valorAnt + deltaValor);
  inv[idx].valor_total    = Math.round(valorNuevo);
  inv[idx].costo_promedio = stock > 0 ? Math.round((valorNuevo / stock) * 100) / 100 : cpAnt;
  if (!inv[idx]._movimientos) inv[idx]._movimientos = [];
  inv[idx]._movimientos.push({
    id: genId(), tipo: "ajuste_costo", fecha: fecha || today(),
    cantidad: 0, costo_unitario: 0,
    costo_promedio_anterior: cpAnt,
    costo_promedio_resultante: inv[idx].costo_promedio,
    stock_resultante: stock,
    valor_total: Math.round(deltaValor),
    origen: origen || null
  });
  saveData("inventario", inv);
  return { costoAnterior: cpAnt, costoPromedio: inv[idx].costo_promedio, stock };
}

export function ajustarInventario(articulo, delta, origen, fecha) {
  if (!articulo || !delta) return;
  if (delta > 0) {
    ingresarInventario(articulo, delta, getCostoPromedio(articulo), origen || { tipo:"AJU", numero:"Ajuste" }, fecha);
  } else {
    egresarInventario(articulo, Math.abs(delta), origen || { tipo:"AJU", numero:"Ajuste" }, fecha);
  }
}

export function getProductosOPSinRemisionar() {
  const result = [];
  (state.data.pedidos||[]).forEach(op => {
    if (op.estado === "cancelada") return;
    const remisiones = (state.data.remisiones||[]).filter(rc => rc._origen && rc._origen.tipo === "OP" && rc._origen.id === op.id && rc.estado !== "cancelada");
    (op.items||[]).forEach(item => {
      const cantOP = parseNumber(item.cantidad) || 0;
      const cantRem = remisiones.reduce((sum, rc) =>
        sum + (rc.items||[]).filter(ri => ri.articulo === item.articulo).reduce((s,ri) => s + (parseNumber(ri.cantidad)||0), 0), 0);
      const pendiente = cantOP - cantRem;
      if (pendiente > 0) {
        result.push({ opNumero: op.numero, opId: op.id, articulo: item.articulo, descripcion: item.descripcion, pendiente,
                      stock: getStockDisponible(item.articulo, op.id) });
      }
    });
  });
  return result;
}

export function getNombreCuenta(codigo) {
  const c = (state.data.puc||[]).find(c => c.codigo === codigo);
  return c ? c.nombre : codigo;
}

// Un documento que llega tarde —la factura del proveedor del 30 que aparece el
// 3— no se puede meter en un mes ya cerrado, pero tampoco se puede perder. Se
// registra en el periodo ABIERTO, con la fecha del día en que se contabiliza,
// dejando anotada la fecha real del documento (`fecha_documento`). Es lo que
// hace un contador: el mes cerrado no se toca y el gasto queda registrado.
//
// Los asientos MANUALES no pasan por aquí sin aviso: quien los escribe elige la
// fecha a propósito, así que la pantalla de Contabilidad los sigue rechazando
// antes de llegar a esta función.
export async function crearAsiento(concepto, origen, movimientos, fecha) {
  const fechaDoc = fecha || todayCO();
  const reclasificado = fechaEstaCerrada(fechaDoc);
  const fechaAsiento = reclasificado ? todayCO() : fechaDoc;
  const movs = movimientos.filter(m => (m.debito||0) > 0 || (m.credito||0) > 0)
    .map(m => ({ cuenta:m.cuenta, nombre:getNombreCuenta(m.cuenta), debito:Math.round(m.debito||0), credito:Math.round(m.credito||0), tercero_id:m.tercero_id||null }));
  const totalDebito = movs.reduce((s,m)=>s+m.debito,0);
  const totalCredito = movs.reduce((s,m)=>s+m.credito,0);
  const asiento = {
    id: genId(),
    numero: await siguienteNumeroDoc("AST"),
    fecha: fechaAsiento,
    // Solo cuando difieren: en el 99% de los asientos son la misma fecha y no
    // hay nada que explicar.
    fecha_documento: reclasificado ? fechaDoc : null,
    concepto, origen, movimientos: movs, totalDebito, totalCredito,
    balanceado: Math.abs(totalDebito - totalCredito) < 1
  };
  saveData("asientos", [...(state.data.asientos||[]), asiento]);
  return asiento;
}

// Retenciones que el cliente (agente retenedor) practica sobre una factura de
// venta. La MISMA fórmula alimenta el asiento y la cartera: si la factura ya
// las trae guardadas (se calculan y persisten al emitir), se usan tal cual —
// así un cambio posterior en la ficha del cliente no desalinea una factura ya
// contabilizada.
export function calcularRetencionesVenta(factura) {
  if (factura && factura.retenciones && typeof factura.retenciones === "object") {
    const r = factura.retenciones;
    const rf = parseNumber(r.retefuente)||0, rica = parseNumber(r.reteica)||0, riva = parseNumber(r.reteiva)||0;
    return { montoRF: rf, montoRICA: rica, montoRIVA: riva, total: rf + rica + riva };
  }
  const subtotal = parseNumber(factura.subtotal) || 0;
  const iva      = parseNumber(factura.iva)      || 0;
  const cliente  = (state.data.clientes||[]).find(c => c.id === factura.cliente_id) || {};
  const rfPct    = parsePct(cliente.retefuente) || 0;  // % sobre subtotal
  const ricaPct  = parsePct(cliente.reteica)    || 0;  // % sobre subtotal
  const rivaPct  = parsePct(cliente.reteiva)    || 0;  // % sobre el IVA

  // Cuantía mínima: por debajo de la base en UVT del concepto, el cliente NO
  // debe practicar retefuente (Dcto. 1625/2016, arts. 1.2.4.x). Sin esto una
  // venta pequeña salía con retención que el cliente nunca practicó, y la
  // cartera quedaba esperando menos plata de la que iban a consignar.
  const pf = getParametrosFiscales();
  const uvt = parseNumber(pf.uvt) || 0;
  const esServicio = (factura.concepto_retencion || "compras") === "servicios";
  const baseUVT = esServicio
    ? (parseNumber(pf.retefuente_servicios_base_uvt) || 0)
    : (parseNumber(pf.retefuente_compras_base_uvt) || 0);
  const baseMinima = baseUVT * uvt;
  const alcanzaBase = !(baseMinima > 0) || subtotal >= baseMinima;

  // La cuantía mínima manda sobre las tres: el ReteIVA solo procede cuando la
  // operación está sujeta a retención por compras o servicios (Art. 437-1 E.T.),
  // y Bogotá adoptó esas mismas bases para el ReteICA (Acuerdo 65 de 2002).
  const montoRF   = (rfPct   > 0 && alcanzaBase) ? Math.round(subtotal * rfPct   / 100) : 0;
  const montoRICA = (ricaPct > 0 && alcanzaBase) ? Math.round(subtotal * ricaPct / 100) : 0;
  const montoRIVA = (rivaPct > 0 && alcanzaBase && iva > 0) ? Math.round(iva * rivaPct / 100) : 0;
  return { montoRF, montoRICA, montoRIVA, total: montoRF + montoRICA + montoRIVA };
}

// Autorretención especial a título de renta (Dcto. 2201 de 2016, hoy DUR
// 1625/2016 arts. 1.2.6.6 a 1.2.6.10). La practica la PROPIA sociedad sobre sus
// ingresos —no la retiene el cliente— cuando está exonerada de aportes
// parafiscales (Art. 114-1 E.T.), y NO reemplaza la retención que le practican
// los clientes: se suma a ella. La tarifa la fija el CIIU de la actividad
// principal (0,40% / 0,80% / 1,60%), por eso vive en Parámetros Fiscales.
export function calcularAutorretencionRenta(subtotal) {
  const pf = getParametrosFiscales();
  if (!pf.autorretencion_renta_activa) return { pct: 0, monto: 0 };
  const pct = parsePct(pf.autorretencion_renta_pct) || 0;
  const base = parseNumber(subtotal) || 0;
  if (pct <= 0 || base <= 0) return { pct, monto: 0 };
  return { pct, monto: Math.round(base * pct / 100) };
}

export async function generarAsientoVenta(factura) {
  const subtotal  = parseNumber(factura.subtotal) || 0;
  const iva       = parseNumber(factura.iva)      || 0;
  const total     = parseNumber(factura.total)    || 0;

  // Obtener retenciones configuradas en el cliente (si es agente de retención)
  const { montoRF, montoRICA, montoRIVA, total: totalRetenciones } = calcularRetencionesVenta(factura);

  // 1305 Clientes CxC: el total de la factura (lo que el cliente debe en total)
  const movimientos = [
    { cuenta:"1305", debito: total, tercero_id: factura.cliente_id },
    { cuenta:"4135", credito: subtotal, tercero_id: factura.cliente_id }
  ];
  if (iva > 0) movimientos.push({ cuenta:"2408", credito: iva, tercero_id: factura.cliente_id });

  // Retenciones practicadas por el cliente → activos (derechos de descuento tributario)
  if (montoRF   > 0) movimientos.push({ cuenta:"1365", debito: montoRF,   tercero_id: factura.cliente_id });
  if (montoRICA > 0) movimientos.push({ cuenta:"1368", debito: montoRICA, tercero_id: factura.cliente_id });
  if (montoRIVA > 0) movimientos.push({ cuenta:"1366", debito: montoRIVA, tercero_id: factura.cliente_id });
  // Contrapartida: reduce la CxC por el monto retenido (el cliente solo pagará el neto)
  if (totalRetenciones > 0) movimientos.push({ cuenta:"1305", credito: totalRetenciones, tercero_id: factura.cliente_id });

  // Autorretención especial de renta: es un anticipo del impuesto de la propia
  // empresa (1365 débito) contra lo que deberá consignar en la declaración
  // mensual de retenciones (2365 crédito). No toca la CxC: el cliente paga
  // igual, la autorretención la asume y declara la empresa.
  const autorret = calcularAutorretencionRenta(subtotal);
  if (autorret.monto > 0) {
    movimientos.push({ cuenta:"1365", debito:  autorret.monto });
    movimientos.push({ cuenta:"2365", credito: autorret.monto });
  }

  // Costo de venta según costo de los productos en maestros (6135 / 1435)
  // Costo de ventas al COSTO PROMEDIO PONDERADO vigente (NIIF Grupo 2 / Sección 13)
  // Se usa getCostoPromedio() que lee el costo promedio actualizado del inventario.
  // Si la factura viene de una remisión que ya descontó inventario al marcarse
  // "completada", no volver a egresar el kardex (evita el doble descuento);
  // el costo contable (6135/1435) sí se registra siempre.
  const _rcOrigen = (factura._origen && factura._origen.tipo === "RC")
    ? (state.data.remisiones||[]).find(r => r.id === factura._origen.id)
    : null;
  const _inventarioYaDescontado = !!(_rcOrigen && _rcOrigen._inventarioDescontado);
  // Facturada por anticipado (sin entregar): la mercancía sigue en bodega, así
  // que todavía no hay costo de ventas ni salida de kardex. Ambos se registran
  // cuando la remisión saque la mercancía — ver registrarCostoEntrega().
  let costoTotal = 0;
  if (!factura._sinEntrega) (factura.items||[]).forEach(it => {
    const cant  = parseNumber(it.cantidad)||0;
    const cp    = getCostoPromedio(it.articulo);   // costo promedio del inventario
    costoTotal += cant * cp;
    // Registrar la salida en el kardex de inventario
    if (!_inventarioYaDescontado) {
      egresarInventario(
        it.articulo, cant,
        { tipo:"FACT", id:factura.id, numero:factura.numero },
        factura.fecha
      );
    }
  });
  if (costoTotal > 0) {
    movimientos.push({ cuenta:"6135", debito:  Math.round(costoTotal) });
    movimientos.push({ cuenta:"1435", credito: Math.round(costoTotal) });
  }

  return await crearAsiento(
    `Venta${totalRetenciones > 0 ? " (con retenciones cliente)" : ""} - Factura ${factura.numero}`,
    { tipo:"FACT", id:factura.id, numero:factura.numero },
    movimientos,
    factura.fecha
  );
}

// Costo de ventas de una entrega cuya mercancía ya se había facturado por
// anticipado. El ingreso quedó en su factura; el costo va aquí, con la fecha en
// que la mercancía salió de bodega, y se descuenta de lo que esa factura tenía
// pendiente por costear. Así el inventario nunca muestra como vendida mercancía
// que todavía está en la bodega, ni el costo se registra dos veces.
export async function registrarCostoEntrega(rc, costos) {
  const opId = (rc && rc._origen && rc._origen.tipo === "OP") ? rc._origen.id : null;
  if (!opId) return null;
  const pendientes = (state.data.facturas || []).filter(f =>
    f.estado !== "anulada" && f._sinEntrega && (f._costoPendiente || []).length &&
    f._origen && f._origen.tipo === "OP" && f._origen.id === opId);
  if (!pendientes.length) return null;

  let costoTotal = 0;
  const tocadas = new Set();
  (costos || []).forEach(({ articulo, cantidad, costoUnitario }) => {
    let porCostear = parseNumber(cantidad) || 0;
    pendientes.forEach(f => {
      if (porCostear <= 0) return;
      (f._costoPendiente || []).forEach(linea => {
        if (porCostear <= 0) return;
        if (normNombre(linea.articulo) !== normNombre(articulo)) return;
        const disponible = parseNumber(linea.cantidad) || 0;
        if (disponible <= 0) return;
        const usa = Math.min(porCostear, disponible);
        linea.cantidad = disponible - usa;
        porCostear -= usa;
        costoTotal += usa * (parseNumber(costoUnitario) || 0);
        tocadas.add(f.id);
      });
    });
  });
  if (!tocadas.size) return null;

  saveData("facturas", (state.data.facturas || []).map(f => tocadas.has(f.id)
    ? { ...f, _costoPendiente: (f._costoPendiente || []).filter(x => (parseNumber(x.cantidad) || 0) > 0) }
    : f));

  if (costoTotal <= 0) return null;
  return await crearAsiento(
    `Costo de ventas - entrega ${rc.numero} (facturada por anticipado)`,
    { tipo: "RC", id: rc.id, numero: rc.numero },
    [
      { cuenta: "6135", debito: Math.round(costoTotal) },
      { cuenta: "1435", credito: Math.round(costoTotal) }
    ],
    rc.fecha
  );
}

export async function generarAsientoCompra(ocp) {
  const prov       = (state.data.proveedores||[]).find(p => p.id === ocp.proveedor_id) || {};
  const subtotal   = parseNumber(ocp.subtotal) || 0;
  const iva        = parseNumber(ocp.iva)      || 0;
  const total      = parseNumber(ocp.total)    || 0;

  // ── Retenciones que la empresa practica al proveedor ──────────────────────────
  const rfPct      = parseNumber(prov.retefuente) || 0;
  const ricaPct    = parseNumber(prov.reteica)    || 0;
  const rivaPct    = parseNumber(prov.reteiva)    || 0;
  const montoRF    = rfPct   > 0 ? Math.round(subtotal * rfPct   / 100) : 0;
  const montoRICA  = ricaPct > 0 ? Math.round(subtotal * ricaPct / 100) : 0;
  const montoRIVA  = rivaPct > 0 && iva > 0 ? Math.round(iva * rivaPct / 100) : 0;
  const totalRet   = montoRF + montoRICA + montoRIVA;

  // Neto que la empresa realmente pagará al proveedor (total factura - retenciones)
  const netoAPagar = total - totalRet;

  // ── Débitos ───────────────────────────────────────────────────────────────
  const movimientos = [];
  if (EMPRESA.regimen_iva === "responsable") {
    // Responsable de IVA: el IVA va a 1355 (descontable) y el inventario solo por el subtotal
    movimientos.push({ cuenta:"1435", debito: subtotal,   tercero_id: ocp.proveedor_id });
    if (iva > 0) movimientos.push({ cuenta:"1355", debito: iva, tercero_id: ocp.proveedor_id });
  } else {
    // No responsable: el IVA no es descontable → se capitaliza al inventario
    movimientos.push({ cuenta:"1435", debito: subtotal + iva, tercero_id: ocp.proveedor_id });
  }

  // ── Créditos ──────────────────────────────────────────────────────────────
  // 2205: solo el neto que se le debe al proveedor (ya descontadas las retenciones)
  movimientos.push({ cuenta:"2205", credito: netoAPagar,  tercero_id: ocp.proveedor_id });
  // Retenciones por pagar a la DIAN (pasivos)
  if (montoRF   > 0) movimientos.push({ cuenta:"2365", credito: montoRF,   tercero_id: ocp.proveedor_id });
  if (montoRIVA > 0) movimientos.push({ cuenta:"2367", credito: montoRIVA, tercero_id: ocp.proveedor_id });
  if (montoRICA > 0) movimientos.push({ cuenta:"2368", credito: montoRICA, tercero_id: ocp.proveedor_id });

  const resumen = totalRet > 0
    ? ` (neto ${fmtCOP(netoAPagar)}, ret. ${fmtCOP(totalRet)})`
    : "";
  return await crearAsiento(
    `Compra${resumen} - OCP ${ocp.numero}`,
    { tipo:"OCP", id:ocp.id, numero:ocp.numero },
    movimientos,
    ocp.fecha
  );
}

export async function generarAsientoPagoOCP(ocp, pago, descuento) {
  const monto = parseNumber(pago.monto) || 0;
  const desc = parseNumber(descuento) || 0;
  const cuentaBanco = pago.medio === "efectivo" ? "1105" : "1110";
  const movimientos = [
    { cuenta:"2205", debito: monto + desc, tercero_id: ocp.proveedor_id },
    { cuenta:cuentaBanco, credito: monto, tercero_id: ocp.proveedor_id }
  ];
  if (desc > 0) movimientos.push({ cuenta:"4210", credito: desc, tercero_id: ocp.proveedor_id });
  return await crearAsiento(`Pago a proveedor - Orden de Compra ${ocp.numero}`, { tipo:"OCP", id:ocp.id, numero:ocp.numero }, movimientos, pago.fecha);
}

export async function generarAsientoCobroFactura(factura, monto, medio, fecha) {
  const cuentaBanco = medio === "efectivo" ? "1105" : "1110";
  const movimientos = [
    { cuenta: cuentaBanco, debito:  monto, tercero_id: factura.cliente_id },
    { cuenta: "1305",      credito: monto, tercero_id: factura.cliente_id }
  ];
  return await crearAsiento(
    `Cobro - Factura ${factura.numero}`,
    { tipo:"FACT", id:factura.id, numero:factura.numero },
    movimientos,
    fecha || today()   // la fecha real del abono, no la del día en que se digita
  );
}

export async function generarAsientoNotaCredito(nc) {
  const subtotal = parseNumber(nc.subtotal) || 0;
  const iva      = parseNumber(nc.iva)      || 0;
  const total    = parseNumber(nc.total)    || 0;

  // ── Reversa de ingresos y CxC ─────────────────────────────────────────────
  const movimientos = [
    { cuenta:"4175", debito:  subtotal, tercero_id: nc.cliente_id }, // devolución reduce ingresos
    { cuenta:"1305", credito: total,    tercero_id: nc.cliente_id }  // cancela CxC
  ];
  if (iva > 0) movimientos.push({ cuenta:"2408", debito: iva, tercero_id: nc.cliente_id }); // revierte IVA generado

  // ── Reversa del costo de venta (los artículos vuelven al inventario) ───────
  let costoDevuelto = 0;
  (nc.items||[]).forEach(it => {
    // Al COSTO PROMEDIO vigente, igual que la venta que se está reversando:
    // usar el costo de catálogo descuadraba 1435/6135 contra el kardex.
    const costo = getCostoPromedio(it.articulo);
    const cant  = parseNumber(it.cantidad) || 0;
    costoDevuelto += cant * costo;
    // Reponer inventario físico, firmado con la nota crédito: la devolución
    // entra al kardex con su documento, no como un "Ajuste" sin explicación.
    if (cant > 0) ajustarInventario(it.articulo, cant,
      { tipo:"NC", id: nc.id, numero: nc.numero }, nc.fecha);
  });
  if (costoDevuelto > 0) {
    movimientos.push({ cuenta:"1435", debito:  costoDevuelto }); // inventario sube
    movimientos.push({ cuenta:"6135", credito: costoDevuelto }); // costo de ventas baja
  }

  return await crearAsiento(
    `Nota Crédito ${nc.numero}${costoDevuelto > 0 ? " (reversa venta + costo)" : ""}`,
    { tipo:"NC", id:nc.id, numero:nc.numero },
    movimientos,
    nc.fecha
  );
}

export async function generarAsientoNomina(liquidacion) {
  const detalle = liquidacion.detalle_empleados || [];

  let salariosBase = 0, comisiones = 0, netoPagar = 0;
  let saludEmpleado = 0, pensionEmpleado = 0, fondoSolidaridad = 0;
  let saludPatronal = 0, pensionPatronal = 0, arl = 0, sena = 0, icbf = 0, cajaCompensacion = 0;
  let cesantias = 0, interesesCesantias = 0, prima = 0, vacaciones = 0;

  detalle.forEach(d => {
    salariosBase += d.devengado.totalDevengado - d.devengado.comisiones;
    comisiones += d.devengado.comisiones;
    netoPagar += d.netoPagar;
    saludEmpleado += d.deducciones.saludEmpleado;
    pensionEmpleado += d.deducciones.pensionEmpleado;
    fondoSolidaridad += d.deducciones.fondoSolidaridad;
    saludPatronal += d.aportesPatronales.saludPatronal;
    pensionPatronal += d.aportesPatronales.pensionPatronal;
    arl += d.aportesPatronales.arl;
    sena += d.aportesPatronales.sena;
    icbf += d.aportesPatronales.icbf;
    cajaCompensacion += d.aportesPatronales.cajaCompensacion;
    cesantias += d.provisionPrestaciones.cesantias;
    interesesCesantias += d.provisionPrestaciones.interesesCesantias;
    prima += d.provisionPrestaciones.prima;
    vacaciones += d.provisionPrestaciones.vacaciones;
  });

  const provisionTotal = cesantias + interesesCesantias + prima + vacaciones;
  const aportesPatronalesTotal = saludPatronal + pensionPatronal + arl + sena + icbf + cajaCompensacion;

  const movimientos = [];
  // ── Débitos: gasto de personal ────────────────────────────────────────────
  if (salariosBase > 0) movimientos.push({ cuenta:"5105", debito: salariosBase });
  if (comisiones > 0) movimientos.push({ cuenta:"5210", debito: comisiones });
  if (provisionTotal > 0) movimientos.push({ cuenta:"5110", debito: provisionTotal });
  if (aportesPatronalesTotal > 0) movimientos.push({ cuenta:"5115", debito: aportesPatronalesTotal });

  // ── Créditos: pasivos por pagar ────────────────────────────────────────────
  if (netoPagar > 0) movimientos.push({ cuenta:"2505", credito: netoPagar });
  const totalEPS = saludEmpleado + saludPatronal;
  if (totalEPS > 0) movimientos.push({ cuenta:"2370", credito: totalEPS });
  const totalPension = pensionEmpleado + pensionPatronal + fondoSolidaridad;
  if (totalPension > 0) movimientos.push({ cuenta:"2371", credito: totalPension });
  if (arl > 0) movimientos.push({ cuenta:"2372", credito: arl });
  const totalParafiscales = sena + icbf + cajaCompensacion;
  if (totalParafiscales > 0) movimientos.push({ cuenta:"2373", credito: totalParafiscales });
  if (cesantias > 0) movimientos.push({ cuenta:"2510", credito: cesantias });
  if (interesesCesantias > 0) movimientos.push({ cuenta:"2515", credito: interesesCesantias });
  if (prima > 0) movimientos.push({ cuenta:"2520", credito: prima });
  if (vacaciones > 0) movimientos.push({ cuenta:"2525", credito: vacaciones });

  const tipoLabel = liquidacion.tipo_periodo === "quincenal" ? "Quincenal" : "Mensual";
  return await crearAsiento(
    `Nómina ${tipoLabel} ${liquidacion.periodo_desde} a ${liquidacion.periodo_hasta}`,
    { tipo:"NOM", id: liquidacion.id, numero: liquidacion.numero },
    movimientos,
    liquidacion.fecha_pago || today()
  );
}

// liquidacion: { id, numero, periodo_desde, periodo_hasta, fecha, detalle_activos:
// [{ activoId, depreciacionContable }] } — un asiento consolidado por período,
// mismo molde que generarAsientoNomina.
export async function generarAsientoDepreciacion(liquidacion) {
  const totalDepreciacion = (liquidacion.detalle_activos || [])
    .reduce((s, d) => s + (parseNumber(d.depreciacionContable) || 0), 0);

  const movimientos = [];
  if (totalDepreciacion > 0) {
    movimientos.push({ cuenta:"5145", debito: totalDepreciacion });
    movimientos.push({ cuenta:"1592", credito: totalDepreciacion });
  }

  return await crearAsiento(
    `Depreciación activos fijos ${liquidacion.periodo_desde} a ${liquidacion.periodo_hasta}`,
    { tipo:"DEP", id: liquidacion.id, numero: liquidacion.numero },
    movimientos,
    liquidacion.fecha || today()
  );
}

// Asiento de baja de un activo (venta, retiro u obsolescencia). `baja` viene
// de calcularBajaActivo() en core/depreciacion.js: { valorEnLibros,
// depreciacionAcumuladaContable, valorVenta, utilidadPerdida, esUtilidad }.
export async function generarAsientoBajaActivo(activo, baja, medio) {
  const costo = parseNumber(activo.costo_adquisicion) || 0;
  const cuentaBanco = medio === "efectivo" ? "1105" : "1110";
  const movimientos = [];

  if (baja.depreciacionAcumuladaContable > 0) movimientos.push({ cuenta:"1592", debito: baja.depreciacionAcumuladaContable });
  if (baja.valorVenta > 0) movimientos.push({ cuenta: cuentaBanco, debito: baja.valorVenta });
  if (!baja.esUtilidad && baja.utilidadPerdida < 0) movimientos.push({ cuenta:"5320", debito: -baja.utilidadPerdida });
  if (costo > 0) movimientos.push({ cuenta: activo.cuenta_activo, credito: costo });
  if (baja.esUtilidad && baja.utilidadPerdida > 0) movimientos.push({ cuenta:"4250", credito: baja.utilidadPerdida });

  return await crearAsiento(
    `Baja de activo fijo - ${activo.nombre}`,
    { tipo:"AF-BAJA", id: activo.id, numero: activo.codigo },
    movimientos,
    today()
  );
}

export function getTipoCuentaPUC(codigo) {
  const map = { "1":"Activo", "2":"Pasivo", "3":"Patrimonio", "4":"Ingreso", "5":"Gasto", "6":"Costo de ventas", "7":"Costo de producción", "8":"Cuentas de orden deudoras", "9":"Cuentas de orden acreedoras" };
  return map[String(codigo)[0]] || "Otro";
}
